import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Package, Activity, Euro, Wallet, ArrowUpRight, MessageSquare, PhoneCall, FileText, Truck, Check, Trash2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { money, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { ClientDashboard } from "@/components/ClientDashboard";
import { DriverDashboard } from "@/components/DriverDashboard";
import { PartnerDashboard } from "@/components/PartnerDashboard";
import { CampaignLinkCard } from "@/components/CampaignLinkCard";

function Kpi({ icon: Icon, label, value, hint, accent, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}><Icon size={18} strokeWidth={1.5} /></span>
      </div>
      <div className="mt-3 font-display text-2xl lg:text-3xl font-bold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>}
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [msgAlerts, setMsgAlerts] = useState(null);
  const [callbacks, setCallbacks] = useState([]);
  const [docAlerts, setDocAlerts] = useState(null);
  const [showCb, setShowCb] = useState(false);
  const isStaff = ["admin", "support"].includes(user?.role);

  const loadCallbacks = () => api.get("/callback-requests").then(({ data }) => setCallbacks(data)).catch(() => {});

  useEffect(() => {
    if (["client", "driver", "partner"].includes(user?.role)) return;
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    if (["admin", "support"].includes(user?.role)) {
      api.get("/compliance/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
      api.get("/chat/unread-summary").then(({ data }) => setMsgAlerts(data)).catch(() => {});
      loadCallbacks();
      api.get("/documents/pending-summary").then(({ data }) => setDocAlerts(data)).catch(() => {});
    }
  }, [user]);

  const markCbRead = async (id) => { try { await api.post(`/callback-requests/${id}/read`); loadCallbacks(); } catch (e) { logError("dashboard:markCbRead", e); } };
  const deleteCb = async (id) => { try { await api.delete(`/callback-requests/${id}`); loadCallbacks(); } catch (e) { logError("dashboard:deleteCb", e); } };

  if (user?.role === "client") return <ClientDashboard />;
  if (user?.role === "driver") return <DriverDashboard />;
  if (user?.role === "partner") return <PartnerDashboard />;

  const chartData = stats ? Object.entries(stats.orders_by_status || {}).map(([k, v]) => ({ status: k.replace(/_/g, " "), count: v })) : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("nav.dashboard")}</h1>
        <p className="text-sm text-slate-500 mt-1">Benvenuto, {user?.name} · <span className="capitalize">{user?.role}</span></p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Euro} label={t("stat.revenue")} value={stats ? money(stats.revenue) : "—"} hint={t("stat.revenueHint")} accent="bg-emerald-50 text-emerald-700" testid="kpi-revenue" />
        <Kpi icon={Package} label={t("stat.ordersValue")} value={stats ? money(stats.orders_value) : "—"} hint={t("stat.ordersValueHint")} accent="bg-blue-50 text-blue-700" testid="kpi-orders-value" />
        <Kpi icon={Wallet} label={t("stat.outstanding")} value={stats ? money(stats.outstanding) : "—"} hint={t("stat.outstandingHint")} accent="bg-amber-50 text-amber-700" testid="kpi-outstanding" />
        {isStaff ? (
          <Kpi icon={Activity} label={t("stat.quotesValue")} value={stats ? money(stats.quotes_value) : "—"} hint={t("stat.quotesValueHint")} accent="bg-slate-100 text-slate-600" testid="kpi-quotes-value" />
        ) : (
          <Kpi icon={Activity} label={t("stat.active")} value={stats?.orders_active ?? "—"} accent="bg-indigo-50 text-indigo-700" testid="kpi-active" />
        )}
      </div>

      {isStaff && (() => {
        const unreadCb = callbacks.filter((c) => !c.read);
        const tiles = [
          { k: "mp", icon: MessageSquare, n: msgAlerts?.partners || 0, label: "Messaggi partner", to: "/orders?filter=unread_partner", color: "text-blue-700 bg-blue-50" },
          { k: "md", icon: MessageSquare, n: msgAlerts?.drivers || 0, label: "Messaggi autisti", to: "/orders?filter=unread_driver", color: "text-indigo-700 bg-indigo-50" },
          { k: "dc", icon: FileText, n: docAlerts?.total || 0, label: "Documenti da revisionare", to: "/orders?filter=docs", color: "text-violet-700 bg-violet-50" },
          { k: "cb", icon: PhoneCall, n: unreadCb.length, label: "Richiamate", onClick: () => setShowCb((s) => !s), color: "text-amber-700 bg-amber-50" },
          { k: "vh", icon: Truck, n: alerts.length, label: "Scadenze veicoli", to: "/vehicles", color: "text-rose-700 bg-rose-50" },
        ].filter((x) => x.n > 0);
        if (tiles.length === 0) return null;
        return (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4" data-testid="dashboard-action-center">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Centro operativo</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {tiles.map((tl) => {
                const Inner = (
                  <div className="flex items-center gap-3 rounded-lg border border-slate-100 p-3 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer h-full">
                    <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${tl.color}`}><tl.icon size={17} strokeWidth={1.5} /></span>
                    <div className="min-w-0">
                      <div className="font-display text-xl font-bold text-slate-900 leading-none">{tl.n}</div>
                      <div className="text-[11px] text-slate-500 mt-1 truncate">{tl.label}</div>
                    </div>
                  </div>
                );
                return tl.to
                  ? <Link key={tl.k} to={tl.to} data-testid={`action-tile-${tl.k}`}>{Inner}</Link>
                  : <button key={tl.k} onClick={tl.onClick} className="text-left" data-testid={`action-tile-${tl.k}`}>{Inner}</button>;
              })}
            </div>

            {showCb && unreadCb.length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-3 space-y-1.5" data-testid="callbacks-list">
                {unreadCb.slice(0, 8).map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 text-sm py-1.5 px-2 rounded-lg hover:bg-slate-50" data-testid="callback-row">
                    <div className="min-w-0">
                      <span className="font-medium text-slate-800">{c.name}</span>
                      <span className="text-slate-500"> · {c.phone}</span>
                      {(c.preferred_date || c.preferred_time) && <span className="text-xs text-slate-400"> · {c.preferred_date} {c.preferred_time}</span>}
                      {c.note && <span className="text-xs text-slate-400 italic"> — {c.note}</span>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <a href={`tel:${c.phone}`} className="text-slate-400 hover:text-emerald-600 p-1" title="Chiama"><PhoneCall size={14} /></a>
                      <button onClick={() => markCbRead(c.id)} className="text-slate-400 hover:text-blue-700 p-1" title="Segna come letto" data-testid="callback-read-btn"><Check size={15} /></button>
                      <button onClick={() => deleteCb(c.id)} className="text-slate-400 hover:text-rose-600 p-1" title="Elimina" data-testid="callback-delete-btn"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Ordini per stato</h3>
          {chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">{t("common.none")}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="status" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="count" fill="#1e3a8a" radius={[6, 6, 0, 0]} barSize={42} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-900">Ordini recenti</h3>
            <Link to="/orders" className="text-blue-700 text-sm flex items-center gap-1 hover:underline">{t("common.view")} <ArrowUpRight size={14} /></Link>
          </div>
          <div className="space-y-3">
            {(stats?.recent_orders || []).length === 0 && <p className="text-sm text-slate-400">{t("common.none")}</p>}
            {(stats?.recent_orders || []).map((o) => (
              <Link to={`/orders/${o.id}`} key={o.id} className="flex items-center justify-between p-2 -mx-2 rounded-lg hover:bg-slate-50">
                <div>
                  <div className="text-sm font-medium text-slate-800">{o.number}</div>
                  <div className="text-xs text-slate-400">{o.origin} → {o.destination}</div>
                </div>
                <StatusBadge status={o.status} />
              </Link>
            ))}
          </div>
        </div>
      </div>

      {isStaff && <CampaignLinkCard />}
    </div>
  );
}
